import React, { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";
import {
  ArrowPathIcon,
  ArrowRightOnRectangleIcon,
  CheckIcon,
  ClockIcon,
  KeyIcon,
  ChevronUpDownIcon
} from "@heroicons/react/24/outline";
import {
  LifecycleCycle,
  LifecycleGroup,
  LifecycleUnit,
  setRentedProspect,
} from "./DataServices/EventHistoryDataService";
import {
  ALL_FLOWS,
  FLOW_LABELS,
  FlowKind,
  TimelineStep,
  availableFlowsForGroup,
  buildSteps,
  eventsForGroup,
  formatDay,
} from "./TimelineModel";

interface Props {
  unit: LifecycleUnit;
  cycles: LifecycleCycle[];
  cycle: LifecycleCycle;
  onSelectCycle: (cycleId: string) => void;
  group: LifecycleGroup | null;
  /** Called after the Rented event's enquirer attribution is changed, so the parent can refetch. */
  onRentedProspectChanged?: () => void;
}

const FLOW_ICONS: Record<FlowKind, typeof ArrowPathIcon> = {
  Renewal: ArrowPathIcon,
  MoveOut: ArrowRightOnRectangleIcon,
  Rental: KeyIcon,
};

const FLOW_TONES: Record<FlowKind, { node: string; line: string; tab: string }> = {
  Renewal: {
    node: "bg-blue-600",
    line: "bg-blue-200",
    tab: "border-blue-500 bg-blue-50 text-blue-700",
  },
  MoveOut: {
    node: "bg-amber-500",
    line: "bg-amber-200",
    tab: "border-amber-500 bg-amber-50 text-amber-700",
  },
  Rental: {
    node: "bg-emerald-600",
    line: "bg-emerald-200",
    tab: "border-emerald-500 bg-emerald-50 text-emerald-700",
  },
};

const cycleLabel = (c: LifecycleCycle) => {
  const reason = c.closedReason ?? "Open";
  const endDate = c.closedAtUtc ? formatDay(c.closedAtUtc) : "Present";
  return `${reason} · ${endDate}`;
};

const StepFacts: React.FC<{ step: TimelineStep }> = ({ step }) => (
  <div className="mt-3 flex flex-wrap gap-2 border-t border-dashed border-gray-200 pt-3">
    {step.facts.map((fact, index) => (
      <span
        key={`${fact.label}-${index}`}
        className={`inline-flex max-w-full items-baseline gap-1.5 rounded-md border px-2 py-1 ${
          fact.strong
            ? "border-blue-200 bg-blue-50"
            : "border-gray-200 bg-gray-50"
        }`}
      >
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {fact.label}
        </span>
        <span
          className={`truncate text-xs ${
            fact.strong ? "font-semibold text-blue-700" : "text-gray-800"
          }`}
          title={fact.value}
        >
          {fact.value}
        </span>
      </span>
    ))}
  </div>
);

const TimelineRow: React.FC<{
  step: TimelineStep;
  flow: FlowKind;
  isLast: boolean;
  expanded: boolean;
  onToggle: () => void;
  scrollTargetRef?: React.RefObject<HTMLLIElement>;
  extra?: React.ReactNode;
}> = ({ step, flow, isLast, expanded, onToggle, scrollTargetRef, extra }) => {
  const tone = FLOW_TONES[flow];
  const canExpand = step.facts.length > 0;

  return (
    <li ref={scrollTargetRef} className="relative flex gap-4 pb-6 last:pb-0">
      {!isLast && (
        <span
          aria-hidden="true"
          className={`absolute left-[11px] top-6 h-full w-px ${
            step.active ? tone.line : "bg-gray-200"
          }`}
        />
      )}

      <span className="relative z-10 mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center">
        {step.active ? (
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-full text-white ${tone.node}`}
          >
            <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
          </span>
        ) : (
          <span className="h-4 w-4 rounded-full border-2 border-gray-300 bg-white" />
        )}
      </span>

      <div
        role={canExpand ? "button" : undefined}
        tabIndex={canExpand ? 0 : undefined}
        aria-expanded={canExpand ? expanded : undefined}
        onClick={canExpand ? onToggle : undefined}
        onKeyDown={
          canExpand
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggle();
                }
              }
            : undefined
        }
        className={`min-w-0 flex-1 rounded-lg border px-3 py-2 transition-colors ${
          canExpand
            ? expanded
              ? "cursor-pointer border-blue-200 bg-blue-50/40"
              : "cursor-pointer border-transparent hover:border-gray-200 hover:bg-gray-50"
            : "border-transparent"
        }`}
      >
        <div className="flex items-center gap-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
            {step.active ? formatDay(step.occurredAt) : "Date TBD"}
          </p>
          {step.active && !step.dateExact && (
            <span
              className="text-[9px] font-medium text-gray-400"
              title="No exact date on file — this is when the sync last saw this milestone, not necessarily when it happened."
            >
              (approx.)
            </span>
          )}
          {step.isLatest && (
            <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
              Latest
            </span>
          )}
        </div>

        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
          <p
            className={`text-sm font-semibold ${
              step.active ? "text-gray-900" : "text-gray-400"
            }`}
          >
            {step.label}
          </p>
          {step.workflow && (
            <span
              className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[9px] font-medium text-gray-500"
              title={`This milestone only ever happens under the ${step.workflow} rental workflow.`}
            >
              {step.workflow} only
            </span>
          )}
        </div>
        <p className="mt-1 max-w-xl text-xs leading-relaxed text-gray-500">
          {step.description}
        </p>

        {canExpand && expanded && <StepFacts step={step} />}

        <p className="mt-1.5 text-[11px] font-medium text-gray-400">
          {step.statusText}
        </p>

        {extra && (
          <div
            className="mt-2 border-t border-dashed border-gray-200 pt-2"
            onClick={(e) => e.stopPropagation()}
          >
            {extra}
          </div>
        )}
      </div>
    </li>
  );
};

const RentedAttribution: React.FC<{
  cycleId: string;
  currentProspectId: string | null;
  prospects: LifecycleGroup[];
  onChanged?: () => void;
}> = ({ cycleId, currentProspectId, prospects, onChanged }) => {
  const [saving, setSaving] = useState(false);
  // A click only arms a pending choice — nothing is written until it's confirmed, so a
  // mis-click can't silently reattribute who rented the apartment.
  const [pendingId, setPendingId] = useState<string | null>(null);

  const pending = prospects.find((p) => p.id === pendingId) ?? null;

  const handleConfirm = async () => {
    if (saving || !pendingId) return;
    setSaving(true);
    try {
      await setRentedProspect(cycleId, pendingId);
      onChanged?.();
    } catch (e) {
      console.error("Failed to set who rented the apartment:", e);
    } finally {
      setSaving(false);
      setPendingId(null);
    }
  };

  if (prospects.length === 0) {
    return (
      <p className="text-[11px] text-gray-400">
        No enquirers recorded in this cycle to attribute this to yet.
      </p>
    );
  }

  if (pending) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] font-medium text-gray-700">
          Mark <span className="font-semibold">{pending.name}</span> as who rented
          this apartment?
        </span>
        <button
          type="button"
          disabled={saving}
          onClick={handleConfirm}
          className="inline-flex items-center rounded-full border border-emerald-500 bg-emerald-600 px-2 py-0.5 text-[11px] font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Confirm"}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => setPendingId(null)}
          className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] font-medium text-gray-500">
        {currentProspectId ? "Rented by:" : "Who rented this apartment?"}
      </span>
      {prospects.map((p) => {
        const active = p.id === currentProspectId;
        return (
          <button
            key={p.id}
            type="button"
            disabled={saving}
            onClick={() => p.id && p.id !== currentProspectId && setPendingId(p.id)}
            aria-pressed={active}
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50 ${
              active
                ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                : "border-gray-200 bg-white text-gray-700 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700"
            }`}
          >
            {p.name}
          </button>
        );
      })}
    </div>
  );
};

const TimelineFlowCanvas: React.FC<Props> = ({
  unit,
  cycles,
  cycle,
  onSelectCycle,
  group,
  onRentedProspectChanged,
}) => {
  const [flow, setFlow] = useState<FlowKind>("Renewal");
  // Only one milestone stays expanded at a time.
  const [expandedStep, setExpandedStep] = useState<string | null>(null);

  // Every group shows all three tabs, in the same order — only which ones are selectable differs.
  const availableFlows = useMemo(
    () => (group ? availableFlowsForGroup(group) : []),
    [group],
  );

  const stepsByFlow = useMemo(() => {
    if (!group) return {} as Record<FlowKind, TimelineStep[]>;
    const events = eventsForGroup(cycle, group);

    return availableFlows.reduce(
      (acc, kind) => {
        acc[kind] = buildSteps(kind, events, cycle.workflowType);
        return acc;
      },
      {} as Record<FlowKind, TimelineStep[]>,
    );
  }, [cycle, group, availableFlows]);

  // Keep the flow valid as the cycle or group selection changes, preferring one with activity.
  // Never lands on a flow this group can't actually run.
  useEffect(() => {
    setExpandedStep(null);
    setFlow((previous) => {
      if (availableFlows.includes(previous) && stepsByFlow[previous]?.some((s) => s.active)) {
        return previous;
      }
      return (
        availableFlows.find((kind) => stepsByFlow[kind]?.some((s) => s.active)) ??
        availableFlows[0] ??
        "Renewal"
      );
    });
  }, [availableFlows, stepsByFlow]);

  const steps = stepsByFlow[flow] ?? [];

  // The single RentalRented event on this cycle, if any — dedupe (cycle+eventType) guarantees at
  // most one. Used to offer the "who rented this" attribution on the Rented step.
  const rentedEvent = useMemo(
    () => cycle.events.find((e) => e.flow === "Rental" && e.eventType === "Rented"),
    [cycle],
  );
  const prospectGroups = useMemo(
    () => cycle.groups.filter((g) => g.kind === "prospect"),
    [cycle],
  );

  // Jump straight to whatever last happened whenever the visible step list changes.
  const latestStepRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    latestStepRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [steps]);

  return (
    <section className="flex h-[60vh] min-h-0 flex-col rounded-xl border border-gray-200 bg-white shadow-sm lg:h-auto">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-3">
        <div>
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="text-base font-semibold text-gray-900">
              {unit.apartmentTitle}
            </h2>
            {cycle.workflowType && (
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                {cycle.workflowType} workflow
              </span>
            )}
          </div>
          {unit.leaseTo && (
            <p className="mt-0.5 text-xs text-gray-500">
              Lease ends {formatDay(unit.leaseTo)}
            </p>
          )}
        </div>

        {/* Cycle selector — only worth showing once there's a choice to make. */}
        {cycles.length > 1 && (
          <div className="relative w-80">
            <Listbox value={cycle.id} onChange={onSelectCycle}>
              <div className="relative mt-1">
                <Listbox.Button className="relative w-full cursor-default rounded-md border border-gray-200 bg-white py-1.5 pl-9 pr-10 text-left shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <ClockIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </span>
                  <span className="block truncate text-xs font-medium text-gray-700">
                    {(() => {
                      const selectedCycle = cycles.find((c) => c.id === cycle.id);
                      if (!selectedCycle) return "Select a cycle";
                      return cycleLabel(selectedCycle);
                    })()}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </span>
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {cycles.map((c, index) => (
                      <Listbox.Option
                        key={c.id}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-3 pr-9 ${
                            active ? "bg-blue-50 text-blue-900" : "text-gray-900"
                          }`
                        }
                        value={c.id}
                      >
                        {({ selected, active }) => (
                          <>
                            <div className="flex items-center justify-between">
                              <span
                                className={`block truncate text-xs ${
                                  selected ? "font-semibold" : "font-normal"
                                }`}
                              >
                                {cycleLabel(c)}
                              </span>
                              {index === 0 && (
                                <span
                                  className={`ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                                    active
                                      ? "bg-blue-200 text-blue-800"
                                      : "bg-blue-100 text-blue-700"
                                  }`}
                                >
                                  Current
                                </span>
                              )}
                            </div>
                            {selected ? (
                              <span
                                className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                                  active ? "text-blue-600" : "text-blue-600"
                                }`}
                              >
                                <CheckIcon className="h-4 w-4" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>
        )}
      </header>

      {/* Flow selector — every group sees all three tabs; unavailable ones are disabled, not hidden. */}
      <div className="flex flex-col gap-2 border-b border-gray-200 px-5 py-3 sm:flex-row">
        {ALL_FLOWS.map((kind) => {
          const available = availableFlows.includes(kind);
          const flowSteps = stepsByFlow[kind] ?? [];
          const activeCount = flowSteps.filter((s) => s.active).length;
          const selected = available && kind === flow;
          const Icon = FLOW_ICONS[kind];

          return (
            <button
              key={kind}
              type="button"
              disabled={!available}
              onClick={() => {
                if (!available) return;
                setFlow(kind);
                setExpandedStep(null);
              }}
              aria-pressed={selected}
              aria-disabled={!available}
              title={available ? undefined : `Not applicable for this ${FLOW_LABELS[kind]} view`}
              className={`flex flex-1 items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors ${
                !available
                  ? "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-400"
                  : selected
                    ? FLOW_TONES[kind].tab
                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              } ${available && activeCount === 0 && !selected ? "opacity-60" : ""}`}
            >
              <span
                className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-white ${
                  !available ? "bg-gray-300" : activeCount > 0 ? FLOW_TONES[kind].node : "bg-gray-300"
                }`}
              >
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">
                  {FLOW_LABELS[kind]}
                </span>
                <span className="block truncate text-[11px] text-gray-500">
                  {available ? `${activeCount} of ${flowSteps.length} milestones` : "Not applicable"}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50/60 px-5 py-5">
        {!group ? (
          <p className="py-10 text-center text-xs text-gray-500">
            Select a tenant or enquirer group on the right to view its flows.
          </p>
        ) : steps.length === 0 ? (
          <p className="py-10 text-center text-xs text-gray-500">
            No milestones to display for this flow yet.
          </p>
        ) : (
          <ol className="mx-auto w-full max-w-2xl">
            {steps.map((step, index) => (
              <TimelineRow
                key={step.key}
                step={step}
                flow={flow}
                isLast={index === steps.length - 1}
                expanded={expandedStep === step.key}
                onToggle={() =>
                  setExpandedStep((current) =>
                    current === step.key ? null : step.key,
                  )
                }
                scrollTargetRef={step.isLatest ? latestStepRef : undefined}
                extra={
                  flow === "Rental" &&
                  step.key === "Rented" &&
                  group?.kind === "prospect" ? (
                    <RentedAttribution
                      cycleId={cycle.id}
                      currentProspectId={rentedEvent?.prospectOrTenantId ?? null}
                      prospects={prospectGroups}
                      onChanged={onRentedProspectChanged}
                    />
                  ) : undefined
                }
              />
            ))}
          </ol>
        )}
      </div>
    </section>
  );
};

export default TimelineFlowCanvas;
